import { useCallback, useEffect, useState } from 'react'
import { useMaxMd } from '@/hooks/useMaxMd'
import { getVestingSchedule } from '@/services/waterfall'
import type { VestingAccount, VestingResponse, VestingSection, VestingStep } from '@/types/waterfall'
import { MEMBER_LABELS } from '@/types/waterfall'
import { formatApiError } from '@/utils/apiError'
import WaterfallTabHeader from '../distribution-waterfall/WaterfallTabHeader'

const fmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
})

const unitsFmt = new Intl.NumberFormat('en-US')

const pctFmt = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const fmtPct = (value: number) => pctFmt.format(value / 100)

const fmtOptional = (value: number | null | undefined) =>
  value != null && Math.abs(value) > 0.005 ? fmt.format(value) : '—'

const statusLabel = {
  completed: 'Completed',
  current: 'Current',
  upcoming: 'Upcoming',
} as const

const DATE_COL_CLASS =
  'sticky left-0 z-20 w-28 min-w-[7rem] max-w-[7rem] border-r border-gray-200 whitespace-nowrap'
const DATE_COL_MOBILE_CLASS =
  'sticky left-0 z-20 w-24 min-w-[6rem] max-w-[6rem] border-r border-gray-200 whitespace-nowrap'
const NUM_COL_CLASS = 'w-[7rem] min-w-[7rem] max-w-[7rem]'
const NUM_COL_MOBILE_CLASS = 'w-[5.5rem] min-w-[5.5rem] max-w-[5.5rem]'
const STATUS_COL_CLASS = 'w-[5.5rem] min-w-[5.5rem] max-w-[5.5rem]'

/** Payable at a milestone: catch-up paid for past/current vests, projected for upcoming. */
function payableOnVest(step: VestingStep): number | null {
  if (step.status === 'upcoming') {
    return step.payable_now
  }
  if (step.paid_catch_up > 0.005) {
    return step.paid_catch_up
  }
  return step.target_at_current_entitlement ?? null
}

function AccountSummary({ account }: { account: VestingAccount }) {
  const totalPayable = account.payable_now + account.deferred_unvested

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <p className="text-xs text-gray-500">Cumulative paid</p>
        <p className="text-xl font-semibold tabular-nums">{fmt.format(account.cumulative_paid)}</p>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <p className="text-xs text-gray-500">Payable now</p>
        <p className="text-xl font-semibold tabular-nums">{fmt.format(account.payable_now)}</p>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <p className="text-xs text-gray-500">Deferred (unvested)</p>
        <p className="text-xl font-semibold tabular-nums">{fmt.format(account.deferred_unvested)}</p>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <p className="text-xs text-gray-500">Withheld balance</p>
        <p className="text-xl font-semibold tabular-nums">{fmt.format(account.withheld_balance)}</p>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <p className="text-xs text-gray-500">Payable from new cash</p>
        <p className="text-xl font-semibold tabular-nums">
          {fmt.format(account.payable_from_new_cash)}
        </p>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <p className="text-xs text-gray-500">Total payable at vest</p>
        <p className="text-xl font-semibold tabular-nums">{fmt.format(totalPayable)}</p>
      </div>
    </div>
  )
}

function MemberSection({ section }: { section: VestingSection }) {
  const maxMd = useMaxMd()
  const cellPadClass = maxMd ? 'px-2 py-2' : 'px-4 py-2'
  const dateColClass = maxMd ? DATE_COL_MOBILE_CLASS : DATE_COL_CLASS
  const numColClass = maxMd ? NUM_COL_MOBILE_CLASS : NUM_COL_CLASS
  const dateHeadClass = `${dateColClass} ${cellPadClass} top-0 z-30 bg-gray-50 text-left font-medium text-gray-600`
  const dateCellClass = (isCurrent: boolean) =>
    `${dateColClass} ${cellPadClass} text-left ${isCurrent ? 'bg-brand-50' : 'bg-white'}`
  const headClass = `${cellPadClass} text-right font-medium text-gray-600 whitespace-nowrap`
  const cellClass = `${cellPadClass} text-right tabular-nums whitespace-nowrap`
  const statusHeadClass = `${STATUS_COL_CLASS} ${cellPadClass} text-left font-medium text-gray-600 whitespace-nowrap`
  const statusCellClass = `${STATUS_COL_CLASS} ${cellPadClass} text-left whitespace-nowrap`

  const label = MEMBER_LABELS[section.member as keyof typeof MEMBER_LABELS] ?? section.member
  const account = section.account
  const upcoming = section.steps.find((s) => s.status === 'upcoming')
  const incrementalPct = upcoming && account
    ? (upcoming.cash_pct ?? upcoming.cumulative_pct) - account.vested_cash_pct
    : 0

  return (
    <section className="rounded-lg border border-gray-200 bg-gray-100 p-4 sm:p-6 space-y-4">
      <header>
        <h3 className="text-base font-semibold text-gray-900">{label}</h3>
        <p className="text-xs text-gray-500">
          Grant {section.grant_date} · {unitsFmt.format(section.total_units)} Class B units
        </p>
      </header>

      {account && (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs text-gray-500">Vested</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {unitsFmt.format(account.vested_units)} units
              </p>
              <p className="text-xs text-gray-500 mt-1">{fmtPct(account.vested_cash_pct)}</p>
            </div>
            {upcoming && (
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <p className="text-xs text-gray-500">Next vest</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums">{upcoming.date}</p>
                <p className="text-xs text-gray-500 mt-1">
                  +{fmtPct(incrementalPct)} vests · {fmt.format(account.payable_at_next_vest)} payable
                </p>
              </div>
            )}
          </div>
          <AccountSummary account={account} />
        </>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <div className="table-scroll-mobile overflow-x-auto overscroll-x-contain">
          <table className="min-w-full text-sm border-separate border-spacing-0">
            <thead className="bg-gray-50">
              <tr>
                <th className={dateHeadClass}>Vest date</th>
                <th className={`${numColClass} ${headClass}`}>Vested units</th>
                <th className={`${numColClass} ${headClass}`}>% Total</th>
                <th className={statusHeadClass}>Status</th>
                <th className={`${numColClass} ${headClass} hidden sm:table-cell`}>Paid catch-up</th>
                <th className={`${numColClass} ${headClass}`}>Payable on vest</th>
                <th className={`${numColClass} ${headClass} hidden md:table-cell`}>Deferred (unvested)</th>
                <th className={`${numColClass} ${headClass} hidden lg:table-cell`}>Total payable at vest</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {section.steps.map((step) => {
                const isCurrent = step.status === 'current'
                return (
                <tr key={step.date} className={isCurrent ? 'bg-brand-50/30' : undefined}>
                  <td className={dateCellClass(isCurrent)}>{step.date}</td>
                  <td className={`${numColClass} ${cellClass}`}>{unitsFmt.format(step.units)}</td>
                  <td className={`${numColClass} ${cellClass}`}>
                    {fmtPct(step.cash_pct ?? step.cumulative_pct)}
                  </td>
                  <td className={`${statusCellClass} ${isCurrent ? 'bg-brand-50/30' : ''}`}>
                    {statusLabel[step.status]}
                  </td>
                  <td className={`${numColClass} ${cellClass} hidden sm:table-cell`}>
                    {step.paid_catch_up > 0 ? fmt.format(step.paid_catch_up) : '—'}
                  </td>
                  <td className={`${numColClass} ${cellClass}`}>
                    {fmtOptional(payableOnVest(step))}
                  </td>
                  <td className={`${numColClass} ${cellClass} text-gray-600 hidden md:table-cell`}>
                    {fmtOptional(step.deferred_unvested)}
                  </td>
                  <td className={`${numColClass} ${cellClass} text-gray-600 hidden lg:table-cell`}>
                    {fmtOptional(step.total_payable_at_vest)}
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}

export default function Vesting() {
  const [data, setData] = useState<VestingResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await getVestingSchedule())
    } catch (e) {
      setError(formatApiError(e, 'Failed to load vesting schedule'))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const sections = data?.sections?.length ? data.sections : []

  return (
    <div className="w-full space-y-6">
      <WaterfallTabHeader
        title="Vesting"
        description="Class B vesting milestones, payable/deferred balances, and catch-up history."
      />

      {loading && <p className="text-sm text-gray-500">Loading vesting schedule…</p>}
      {error && (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      {sections.map((section) => (
        <MemberSection key={section.member} section={section} />
      ))}

      {data && sections.length === 0 && data.steps.length > 0 && (
        <MemberSection
          section={{
            member: 'SM',
            grant_date: data.asof,
            total_units: data.total_sm_units,
            account: data.accounts[0] ?? null,
            steps: data.steps,
          }}
        />
      )}
    </div>
  )
}
